import { useState, useEffect } from 'react';

const PRICE_LADDER = [3.69, 5.55, 6.39, 7.77];
const RETAIL_PRICE = 14.99;
const STORAGE_KEY = 'discovery_pieces_explored';
const VISITOR_KEY = 'discovery_visitor_id';
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or retrieve a stable visitor ID
function getVisitorId() {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = Math.random().toString(36).substring(2, 14);
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return Math.random().toString(36).substring(2, 14);
  }
}

// Fire-and-forget analytics event
export function trackDiscovery(eventType, extra = {}) {
  const visitorId = getVisitorId();
  fetch(`${API_URL}/api/analytics/event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ visitor_id: visitorId, event_type: eventType, ...extra }),
  }).catch(() => {});
}

export function useDiscoveryPrice() {
  const [piecesExplored, setPiecesExplored] = useState(() => {
    try { return parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10); } catch { return 0; }
  });

  const recordExploration = () => {
    const next = piecesExplored + 1;
    setPiecesExplored(next);
    localStorage.setItem(STORAGE_KEY, String(next));
    return next;
  };

  const currentPrice = piecesExplored === 0
    ? PRICE_LADDER[0]
    : PRICE_LADDER[Math.min(piecesExplored, PRICE_LADDER.length - 1)];

  const nextPrice = PRICE_LADDER[Math.min(piecesExplored + 1, PRICE_LADDER.length - 1)];
  const savings = RETAIL_PRICE - currentPrice;
  const isMaxPrice = piecesExplored >= PRICE_LADDER.length - 1;

  return { piecesExplored, currentPrice, nextPrice, savings, retailPrice: RETAIL_PRICE, isMaxPrice, recordExploration };
}

export function DiscoveryHookModal({ isOpen, onClose, currentPrice, nextPrice, savings, retailPrice, isMaxPrice, piecesExplored, onBuy, onSignup, sourcePage, pieceTitle }) {
  const [animatePrice, setAnimatePrice] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimatePrice(true), 300);
      // Track modal view
      trackDiscovery('hook_modal_view', {
        price_shown: currentPrice,
        price_tier: PRICE_LADDER.indexOf(currentPrice),
        pieces_explored: piecesExplored,
        piece_title: pieceTitle || null,
        source_page: sourcePage || 'treasure_map',
      });
      return () => setAnimatePrice(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const analyticsBase = {
    price_shown: currentPrice,
    price_tier: PRICE_LADDER.indexOf(currentPrice),
    pieces_explored: piecesExplored,
    piece_title: pieceTitle || null,
    source_page: sourcePage || 'treasure_map',
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="discovery-hook-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => { trackDiscovery('hook_dismiss', analyticsBase); onClose(); }} />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-gradient-to-b from-[#0f0a04] to-[#1a1208] border border-amber-800/40 rounded-2xl shadow-2xl overflow-hidden"
        style={{ boxShadow: '0 0 80px rgba(217,119,6,0.15), 0 25px 50px rgba(0,0,0,0.5)' }}>

        {/* Glow accent */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent rounded-full" />

        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="text-amber-600/60 text-xs tracking-[0.3em] uppercase font-serif mb-2">
              Your Discovery Price
            </div>
            <h2 className="text-2xl font-serif text-amber-100 mb-1">
              You've found something rare
            </h2>
            <p className="text-amber-700/80 text-sm">
              The deeper you explore, the more this knowledge is worth
            </p>
          </div>

          {/* Price Display */}
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <div className="text-amber-500/40 text-sm line-through mb-1">
                Retail: ${retailPrice.toFixed(2)}
              </div>
              <div className={`text-5xl font-bold transition-all duration-700 ${animatePrice ? 'text-amber-400 scale-100' : 'text-amber-600 scale-95'}`}
                data-testid="discovery-price">
                ${currentPrice.toFixed(2)}
              </div>
              <div className="text-emerald-500 text-sm font-semibold mt-1" data-testid="discovery-savings">
                You save ${savings.toFixed(2)}
              </div>
              {!isMaxPrice && (
                <div className="mt-3 px-3 py-1.5 bg-red-900/30 border border-red-800/40 rounded-lg inline-block">
                  <span className="text-red-400 text-xs font-medium" data-testid="price-warning">
                    Next piece: price rises to ${nextPrice.toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Two Paths */}
          <div className="space-y-3">
            {/* Path 1: Buy Book */}
            <button
              onClick={() => { trackDiscovery('hook_buy', analyticsBase); onBuy(); }}
              data-testid="discovery-buy-btn"
              className="w-full py-4 px-6 rounded-xl font-semibold text-base transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: 'linear-gradient(135deg, #d97706, #b45309)',
                color: '#000',
                boxShadow: '0 4px 20px rgba(217,119,6,0.3), inset 0 1px 0 rgba(255,255,255,0.15)',
              }}
            >
              <div className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Get the Book — ${currentPrice.toFixed(2)}
              </div>
              <div className="text-xs opacity-80 mt-0.5">Lock in your discovery price now</div>
            </button>

            {/* Path 2: Free Seeker */}
            <button
              onClick={() => { trackDiscovery('hook_signup', analyticsBase); onSignup(); }}
              data-testid="discovery-signup-btn"
              className="w-full py-3.5 px-6 rounded-xl font-medium text-sm transition-all duration-300 hover:bg-amber-900/20 border border-amber-800/30 text-amber-400 hover:text-amber-300"
            >
              <div className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                </svg>
                Continue exploring free as Seeker
              </div>
            </button>
          </div>

          {/* Skip (subtle) */}
          <button
            onClick={() => { trackDiscovery('hook_dismiss', analyticsBase); onClose(); }}
            className="w-full mt-4 text-center text-amber-500/60 text-xs hover:text-amber-400 transition-colors"
            data-testid="discovery-skip-btn"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
